import { TradeStatusBadge } from '@/entities/business-relationship/ui/TradeStatusBadge';
import type {
  PrescriptionRegistrationDetailParams,
  RegistrationMonth,
  RegistrationTrade,
} from '@/entities/prescription-registration/api/prescription-registration.api';
import { usePrescriptionRegistrationDetail } from '@/entities/prescription-registration/hooks/usePrescriptionRegistrations';
import { formatRegistrationMonth } from '@/entities/prescription-registration/registrationMonth';
import { RegistrationStatusBadge } from '@/entities/prescription-registration/ui/RegistrationStatusBadge';
import { getApiErrorMessage } from '@/shared/api/apiError';
import { formatDate, orDash, toSigungu } from '@/shared/lib/format';
import {
  Badge,
  DataTable,
  Drawer,
  DrawerSection,
  Spinner,
  type DataTableColumn,
} from '@/shared/ui';

interface RegistrationDetailPanelProps {
  /** null이면 닫힘 */
  params: PrescriptionRegistrationDetailParams | null;
  onClose: () => void;
}

/** 거래 중 → 거래 종료, 그다음 제약사명 순 (기획 07-1 메모 7) */
const sortTrades = (trades: RegistrationTrade[]) =>
  [...trades].sort(
    (a, b) =>
      Number(a.tradeStatus === 'ENDED') - Number(b.tradeStatus === 'ENDED') ||
      (a.pharmaCompanyName ?? '').localeCompare(
        b.pharmaCompanyName ?? '',
        'ko',
      ),
  );

const MONTH_COLUMNS: DataTableColumn<RegistrationMonth>[] = [
  {
    key: 'registrationYearMonth',
    header: '처방 등록 월',
    render: (month) => formatRegistrationMonth(month.registrationYearMonth),
  },
  {
    key: 'settlementYearMonth',
    header: '정산 월',
    render: (month) => formatRegistrationMonth(month.settlementYearMonth),
  },
  {
    key: 'registrationStatus',
    header: '등록 상태',
    render: (month) => (
      <RegistrationStatusBadge status={month.registrationStatus} />
    ),
  },
  {
    key: 'imageCount',
    header: '등록 파일',
    render: (month) =>
      month.imageCount > 0 ? (
        `${month.imageCount}장`
      ) : (
        <span className="text-fg-tertiary">-</span>
      ),
  },
];

const TRADE_COLUMNS: DataTableColumn<RegistrationTrade>[] = [
  {
    key: 'pharmaCompanyName',
    header: '제약사명',
    render: (trade) => orDash(trade.pharmaCompanyName),
  },
  {
    key: 'drugName',
    header: '의약품명',
    width: 220,
    render: (trade) => (
      <DrugName drugCode={trade.drugCode} drugName={trade.drugName} />
    ),
  },
  {
    key: 'tradeStatus',
    header: '거래 상태',
    width: 120,
    render: (trade) => <TradeStatusBadge {...trade} />,
  },
];

/**
 * 07-1 처방 등록 상세 (우측 드로어).
 * 07·06 카드 클릭으로 연다. 담당자 × 병의원 1건의 최근 6개월 등록 상태 + 거래 제약사·의약품.
 * × 버튼으로만 닫는다(딤 클릭 · ESC 로는 닫히지 않는다).
 */
export function RegistrationDetailPanel({
  params,
  onClose,
}: RegistrationDetailPanelProps) {
  const { data, isLoading, error } = usePrescriptionRegistrationDetail(params);

  // 값이 없는 칸도 빼지 않고 '-' 로 보인다
  const headerMeta = data
    ? [
        `요양기관 ${orDash(data.ykiho)}`,
        toSigungu(data.region),
        `담당 ${orDash(data.userCompanyName)}${data.userRepresentativeName ? `(${data.userRepresentativeName})` : ''}`,
      ]
    : [];

  return (
    <Drawer
      open={params !== null}
      onClose={onClose}
      label="처방 등록 상세"
      title={
        <span className="flex items-center gap-2">
          {isLoading && !data ? '' : orDash(data?.clinicName)}
          {data?.clinicClosedOn && (
            <Badge tone="error">폐업 {formatDate(data.clinicClosedOn)}</Badge>
          )}
        </span>
      }
      description={headerMeta.length > 0 ? headerMeta.join(' · ') : undefined}
    >
      {isLoading && !data ? (
        <div
          role="status"
          className="flex flex-col items-center gap-3 py-16 text-body-14 font-medium text-fg-light"
        >
          <Spinner size="lg" />
          불러오는 중…
        </div>
      ) : error ? (
        <p className="text-body-14 font-medium text-fg-error">
          {getApiErrorMessage(error, '상세 정보를 불러오지 못했어요.')}
        </p>
      ) : (
        data && (
          <>
            <DrawerSection title="최근 6개월 등록 상태" caption="최신순">
              <DataTable
                columns={MONTH_COLUMNS}
                rows={data.months}
                getRowKey={(month) => month.registrationYearMonth}
                isRowSelected={(month) => month.selected}
                emptyMessage="표시할 월이 없어요."
              />
            </DrawerSection>

            <DrawerSection
              title="거래 제약사 · 의약품"
              caption={`${data.trades.length}건`}
            >
              <DataTable
                columns={TRADE_COLUMNS}
                rows={sortTrades(data.trades)}
                getRowKey={(trade) =>
                  `${trade.relationPharmaCompanyId}:${trade.drugCode ?? '*'}`
                }
                emptyMessage="현재 거래 중인 제약사·의약품이 없어요."
              />
            </DrawerSection>
          </>
        )
      )}
    </Drawer>
  );
}

/** 제약사 단위(전 품목) 거래는 회색 '전 품목'. 의약품명이 없으면 코드, 둘 다 없으면 '-' */
export function DrugName({
  drugCode,
  drugName,
}: {
  drugCode: string | null;
  drugName: string | null;
}) {
  if (drugCode == null) return <span className="text-fg-light">전 품목</span>;
  return <>{orDash(drugName ?? drugCode)}</>;
}
